import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ArrowLeft, Save, MessageSquare, Printer, FileImage, File, FileText, ExternalLink, AlertTriangle } from "lucide-react";
import { JournalEntry, JournalAttachment } from "@/types/journal";
import { formatDate } from "@/utils/dateFormat";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useIdleLogoutWithWarning } from "@/hooks/useIdleLogoutWithWarning";

const TherapistJournalEntryView = () => {
  const navigate = useNavigate();
  const { clientId, entryId } = useParams<{ clientId: string; entryId: string }>();
  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingNote, setSavingNote] = useState(false);
  const [therapistNote, setTherapistNote] = useState("");
  const [clientName, setClientName] = useState("");
  const [attachments, setAttachments] = useState<JournalAttachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  // Idle logout hook for therapists only
  const { showWarning, countdown } = useIdleLogoutWithWarning({
    logout: async () => {
      await supabase.auth.signOut();
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      navigate('/auth');
    },
  });

  useEffect(() => {
    const fetchEntryAndClient = async () => {
      try {
        setLoading(true);
        
        // Fetch the journal entry
        const { data: entryData, error: entryError } = await supabase
          .from("journal_entries")
          .select("*")
          .eq("id", entryId)
          .single();

        if (entryError) throw entryError;
        
        // Ensure media_type is correctly typed
        const typedEntry = {
          ...entryData,
          media_type: entryData.media_type === 'audio' || entryData.media_type === 'video' 
            ? entryData.media_type as 'audio' | 'video' 
            : null
        } as JournalEntry;
        
        setEntry(typedEntry);

        // Fetch client name
        const { data: clientData, error: clientError } = await supabase
          .from("user_profiles")
          .select("first_name, last_name")
          .eq("user_id", clientId)
          .single();

        if (clientError) throw clientError;
        setClientName(`${clientData.first_name || ""} ${clientData.last_name || ""}`.trim());

        // Fetch existing therapist note
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: notes, error: notesError } = await supabase
            .rpc('get_therapist_notes_for_client', {
              client_id_param: clientId
            });

          if (!notesError && notes) {
            const note = notes.find((n: any) => n.entry_id === entryId);
            if (note) {
              setTherapistNote(note.content || "");
            }
          }
        }

        // Fetch attachments
        setLoadingAttachments(true);
        const { data: attachmentsData, error: attachmentsError } = await supabase.rpc('get_journal_attachments', {
          entry_id: entryId
        });

        if (attachmentsError) throw attachmentsError;
        setAttachments(attachmentsData as JournalAttachment[]);
      } catch (error) {
        console.error("Error fetching entry:", error);
        toast.error("Failed to load journal entry");
      } finally {
        setLoading(false);
        setLoadingAttachments(false);
      }
    };

    if (clientId && entryId) {
      fetchEntryAndClient();
    }
  }, [clientId, entryId]);

  const handleSaveNote = async () => {
    try {
      setSavingNote(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to save notes");
        return;
      }

      const { error } = await supabase.rpc('save_therapist_note', {
        therapist_id_param: user.id,
        client_id_param: clientId,
        entry_id_param: entryId,
        content_param: therapistNote
      });

      if (error) throw error;
      
      toast.success("Note saved successfully");
    } catch (error: any) {
      console.error("Error saving note:", error);
      toast.error(error.message || "Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleViewAttachment = async (attachment: JournalAttachment) => {
    try {
      setLoadingStates(prev => ({ ...prev, [attachment.id]: true }));
      
      const { data } = await supabase.storage
        .from('journal_attachments')
        .createSignedUrl(attachment.file_path, 60 * 5); // 5 minutes
      
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      } else {
        toast.error('Failed to generate file URL');
      }
    } catch (error) {
      console.error('Error generating file URL:', error);
      toast.error('Failed to open file');
    } finally {
      setLoadingStates(prev => ({ ...prev, [attachment.id]: false }));
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <FileImage className="h-5 w-5" />;
    } else if (mimeType === 'application/pdf') {
      return <File className="h-5 w-5" />;
    } else if (mimeType === 'text/plain') {
      return <FileText className="h-5 w-5" />;
    } else {
      return <File className="h-5 w-5" />;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#E8F0F8] to-white flex items-center justify-center p-4">
        <Card className="max-w-md mx-auto shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-red-600">Entry Not Found</CardTitle>
            <CardDescription>The requested journal entry could not be found.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate(-1)} 
              className="w-full"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isNegativeEmotion = ['Sad', 'Angry', 'Frustrated', 'Anxious', 'Guilty', 'Overwhelmed'].includes(entry.emotion_type || '');

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8F0F8] to-white">
      {/* Idle warning modal */}
      <Dialog open={showWarning}>
        <DialogContent className="bg-gradient-to-br from-yellow-50 to-white border-yellow-200 shadow-2xl">
          <div className="flex flex-col items-center text-center space-y-4">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mb-2 animate-bounce" />
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-yellow-700">Are you still there?</DialogTitle>
              <DialogDescription className="text-base text-yellow-900">
                For your security, you will be logged out in
                <span className="mx-2 px-3 py-1 rounded-lg bg-yellow-100 text-yellow-700 font-extrabold text-xl border border-yellow-300 animate-pulse">
                  {countdown}
                </span>
                seconds due to inactivity.<br />
                <span className="text-sm text-yellow-700">Click or press any key to stay logged in.</span>
              </DialogDescription>
            </DialogHeader>
          </div>
        </DialogContent>
      </Dialog>
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Journal Entries
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Journal Entry */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl text-[#333B44]">{entry.mood || 'Unspecified Mood'}</CardTitle>
                  <CardDescription>
                    {clientName} • {formatDate(entry.created_at)}
                  </CardDescription>
                </div>
                {entry.emotion_type && (
                  <div className="text-right">
                    <div className="font-medium text-[#3BAFDA]">{entry.emotion_type}</div>
                    <div className="text-sm text-muted-foreground">
                      Intensity: {entry.emotion_intensity}/10
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="prose max-w-none">
                <p className="whitespace-pre-wrap text-[#333B44]">{entry.content}</p>
              </div>
            </CardContent>
          </Card>

          {/* Therapist Notes */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
              <CardTitle className="text-xl text-[#333B44] flex items-center">
                <MessageSquare className="mr-2 h-5 w-5" />
                Your Notes
              </CardTitle>
              <CardDescription>Add your observations and feedback</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <Textarea
                value={therapistNote}
                onChange={(e) => setTherapistNote(e.target.value)}
                placeholder="Add your notes here..."
                className="min-h-[300px] resize-none"
              />
            </CardContent>
            <CardFooter className="bg-[#E8F0F8]/50 rounded-b-lg border-t border-[#CCCCCC] px-6 py-4">
              <Button
                onClick={handleSaveNote}
                disabled={savingNote}
                className="w-full bg-[#3BAFDA] hover:bg-[#333B44] text-white"
              >
                {savingNote ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Notes
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Attachments - only show if they exist */}
        {loadingAttachments ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-[#3BAFDA]" />
            <span className="ml-2 text-sm text-[#333B44]">Loading attachments...</span>
          </div>
        ) : attachments.length > 0 && (
          <div className={`mt-8 border ${isNegativeEmotion ? 'border-red-100' : 'border-[#CCCCCC]'} rounded-lg p-4 bg-white`}>
            <h3 className="text-lg font-medium mb-4">Attached Files</h3>
            <div className="space-y-3">
              {attachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center justify-between bg-white p-3 rounded-md border border-[#CCCCCC]">
                  <div className="flex items-center">
                    <span className="text-[#3BAFDA] mr-3">
                      {getFileIcon(attachment.mime_type)}
                    </span>
                    <div>
                      <p className="text-sm font-medium truncate max-w-[200px] sm:max-w-[300px]">
                        {attachment.filename}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(attachment.file_size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleViewAttachment(attachment)}
                    disabled={loadingStates[attachment.id]}
                    className="h-8 w-8 p-0"
                  >
                    {loadingStates[attachment.id] ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ExternalLink className="h-4 w-4" />
                    )}
                    <span className="sr-only">View</span>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TherapistJournalEntryView; 